import { useState, useEffect } from 'react';
import supabase from '@/lib/supabase';
import { useAuth } from './auth';

export interface DbNotification {
  id: string;
  title: string;
  content: string;
  type: string;
  created_at: string;
  read: boolean;
  link?: string;
  icon?: string;
  sender_id: string;
  sender_name?: string;
}

export function useDbNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<DbNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications when user is authenticated
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      setLoading(true);
      setError(null);

      try {
        // First, get the employee id for the current user
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (employeeError || !employeeData) {
          console.error('Error finding employee data:', employeeError);
          setError('Could not find employee data');
          setLoading(false);
          return;
        }

        const employeeId = employeeData.id;

        // Fetch all broadcast notifications (sent to everyone)
        const { data: broadcastData, error: broadcastError } = await supabase
          .from('notifications')
          .select(`
            id, 
            title, 
            content, 
            type, 
            created_at, 
            link, 
            icon, 
            sender_id,
            sender:sender_id(first_name, last_name)
          `)
          .eq('is_broadcast', true)
          .order('created_at', { ascending: false });

        if (broadcastError) {
          console.error('Error fetching broadcast notifications:', broadcastError);
          setError('Failed to load broadcast notifications');
          setLoading(false);
          return;
        }

        // Fetch targeted notifications for this employee
        const { data: targetedData, error: targetedError } = await supabase
          .from('notification_recipients')
          .select(`
            notification_id,
            read,
            read_at,
            notification:notification_id(
              id, 
              title, 
              content, 
              type, 
              created_at, 
              link, 
              icon, 
              sender_id,
              sender:sender_id(first_name, last_name)
            )
          `)
          .eq('recipient_id', employeeId)
          .order('created_at', { ascending: false });

        if (targetedError) {
          console.error('Error fetching targeted notifications:', targetedError);
          setError('Failed to load targeted notifications');
          setLoading(false);
          return;
        }

        // Process broadcast notifications
        const processedBroadcast = broadcastData.map(notification => {
          // Check if this broadcast notification has an entry in recipients table
          const recipientEntry = targetedData?.find(item => 
            item.notification?.id === notification.id
          );

          return {
            id: notification.id,
            title: notification.title,
            content: notification.content,
            type: notification.type,
            created_at: notification.created_at,
            link: notification.link,
            icon: notification.icon,
            sender_id: notification.sender_id,
            sender_name: notification.sender ? 
              `${notification.sender.first_name} ${notification.sender.last_name}` : 
              undefined,
            read: recipientEntry ? !!recipientEntry.read : false
          };
        });

        // Process targeted notifications
        const processedTargeted = targetedData
          .filter(item => item.notification) // Filter out any null notifications
          .map(item => {
            const notification = item.notification;
            return {
              id: notification.id,
              title: notification.title,
              content: notification.content,
              type: notification.type,
              created_at: notification.created_at,
              link: notification.link,
              icon: notification.icon,
              sender_id: notification.sender_id,
              sender_name: notification.sender ? 
                `${notification.sender.first_name} ${notification.sender.last_name}` : 
                undefined,
              read: !!item.read
            };
          });

        // Combine and deduplicate notifications
        const combinedNotifications = [...processedTargeted];
        
        // Add broadcast notifications only if they don't already exist in the targeted list
        processedBroadcast.forEach(broadcast => {
          if (!combinedNotifications.some(n => n.id === broadcast.id)) {
            combinedNotifications.push(broadcast);
          }
        });

        // Sort by creation date (newest first)
        combinedNotifications.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setNotifications(combinedNotifications);
        setUnreadCount(combinedNotifications.filter(n => !n.read).length);
      } catch (err) {
        console.error('Error in useDbNotifications hook:', err);
        setError('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Set up realtime subscription for new notifications
    const notificationsSubscription = supabase
      .channel('notifications-channel')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'notifications' 
      }, () => {
        fetchNotifications();
      })
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'notification_recipients' 
      }, () => {
        fetchNotifications();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(notificationsSubscription);
    };
  }, [user]);

  // Mark a notification as read
  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    try {
      // Get employee ID
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (employeeError || !employeeData) {
        console.error('Error finding employee:', employeeError);
        return;
      }

      const employeeId = employeeData.id;

      // Check if there's already an entry in notification_recipients
      const { data: existingEntry, error: checkError } = await supabase
        .from('notification_recipients')
        .select('id')
        .eq('notification_id', notificationId)
        .eq('recipient_id', employeeId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking notification recipient entry:', checkError);
        return;
      }

      if (existingEntry) {
        // Update existing entry
        const { error: updateError } = await supabase
          .from('notification_recipients')
          .update({ 
            read: true,
            read_at: new Date().toISOString()
          })
          .eq('id', existingEntry.id);

        if (updateError) {
          console.error('Error updating notification read status:', updateError);
          return;
        }
      } else {
        // Create a new entry
        const { error: insertError } = await supabase
          .from('notification_recipients')
          .insert({
            notification_id: notificationId,
            recipient_id: employeeId,
            read: true,
            read_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error creating notification recipient entry:', insertError);
          return;
        }
      }

      // Update local state
      setNotifications(prev => prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true } 
          : notification
      ));
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user || notifications.length === 0) return;

    try {
      // Get employee ID
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (employeeError || !employeeData) {
        console.error('Error finding employee:', employeeError);
        return;
      }

      const employeeId = employeeData.id;
      const unreadNotificationIds = notifications
        .filter(n => !n.read)
        .map(n => n.id);

      // For each unread notification
      for (const notificationId of unreadNotificationIds) {
        // Check if there's already an entry
        const { data: existingEntry, error: checkError } = await supabase
          .from('notification_recipients')
          .select('id')
          .eq('notification_id', notificationId)
          .eq('recipient_id', employeeId)
          .maybeSingle();

        if (checkError) {
          console.error('Error checking notification recipient entry:', checkError);
          continue;
        }

        if (existingEntry) {
          // Update existing entry
          await supabase
            .from('notification_recipients')
            .update({ 
              read: true,
              read_at: new Date().toISOString()
            })
            .eq('id', existingEntry.id);
        } else {
          // Create a new entry
          await supabase
            .from('notification_recipients')
            .insert({
              notification_id: notificationId,
              recipient_id: employeeId,
              read: true,
              read_at: new Date().toISOString()
            });
        }
      }

      // Update local state
      setNotifications(prev => prev.map(notification => ({ 
        ...notification, 
        read: true 
      })));
      
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead
  };
}